import { useEffect, useCallback, useRef, useState } from 'react'
import { Room, ConnectionState, RoomEvent } from 'livekit-client'

import type { MeetingUserChoices } from '@/src/hooks/useMeetingConsent'
import { useIsMobile } from '@/src/hooks/useIsMobile'

// Генерируем уникальный ID для каждой вкладки при первой загрузке
const getTabId = (): string => {
  if (typeof window === 'undefined') return 'default'

  const STORAGE_KEY = 'lk-tab-id'
  let tabId = sessionStorage.getItem(STORAGE_KEY)

  if (!tabId) {
    // Генерируем уникальный ID: timestamp + random
    tabId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    sessionStorage.setItem(STORAGE_KEY, tabId)
  }

  return tabId
}

const getMeetingSessionKey = (): string => {
  const tabId = getTabId()
  return `lk-meeting-session-${tabId}`
}

const SESSION_EXPIRY_MS = 30 * 60 * 1000 // 30 минут - сессия считается устаревшей

interface MeetingSession {
  roomName: string
  username: string
  audioEnabled: boolean
  videoEnabled: boolean
  audioDeviceId?: string
  videoDeviceId?: string
  timestamp: number
  wasConnected: boolean
  meetingConsent?: MeetingUserChoices['meetingConsent']
}

// Проверяет, была ли активная сессия встречи (для авто-входа при перезагрузке)
export function getSavedMeetingSession(): MeetingSession | null {
  if (typeof sessionStorage === 'undefined') return null

  try {
    const sessionKey = getMeetingSessionKey()
    const saved = sessionStorage.getItem(sessionKey)
    if (!saved) return null

    const session: MeetingSession = JSON.parse(saved)

    // Проверяем что сессия не устарела
    const now = Date.now()
    if (now - session.timestamp > SESSION_EXPIRY_MS) {
      console.log('[MeetingSession] Session expired, clearing')
      sessionStorage.removeItem(getMeetingSessionKey())
      return null
    }

    // Проверяем что пользователь был подключен
    if (!session.wasConnected) {
      return null
    }

    return session
  } catch (e) {
    console.error('[MeetingSession] Error reading session:', e)
    return null
  }
}

// Проверяет, нужен ли авто-вход (сессия существует и roomName совпадает)
export function shouldAutoRejoin(currentRoomName: string): MeetingSession | null {
  const session = getSavedMeetingSession()

  if (!session) return null

  // Проверяем что это та же комната
  if (session.roomName !== currentRoomName) {
    console.log('[MeetingSession] Different room, not auto-rejoining')
    return null
  }

  console.log('[MeetingSession] Found valid session for auto-rejoin')
  return session
}

// Конвертирует сохранённую сессию в настройки участника.
export function sessionToUserChoices(session: MeetingSession): MeetingUserChoices {
  return {
    username: session.username,
    audioEnabled: session.audioEnabled,
    videoEnabled: session.videoEnabled,
    audioDeviceId: session.audioDeviceId || '',
    videoDeviceId: session.videoDeviceId || '',
    meetingConsent: session.meetingConsent,
  }
}

// Очищает сохранённую сессию
export function clearMeetingSession(): void {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(getMeetingSessionKey())
      console.log('[MeetingSession] Session cleared')
    }
  } catch (e) { console.error('[MeetingSession] Error clearing session:', e) }
}

const FORCE_PREJOIN_SESSION_KEY = 'lk-force-prejoin'

/**
 * Просит следующую загрузку страницы показать форму входа, а не авто-вход.
 *
 * Вызывается перед принудительной перезагрузкой с экрана ошибки подключения.
 * Флаг живёт в sessionStorage этой вкладки, поэтому переживает перезагрузку,
 * но не может просочиться в другие вкладки или в завтрашнюю сессию.
 */
export function requestForcePrejoin(): void {
  if (typeof sessionStorage === 'undefined') return

  try {
    sessionStorage.setItem(FORCE_PREJOIN_SESSION_KEY, '1')
  } catch (e) {
    // sessionStorage может быть недоступен (приватный режим, ограничения браузера):
    // тогда авто-вход просто отработает как раньше, хуже не станет
    console.error('[MeetingSession] Error saving force-prejoin flag:', e)
  }
}

/**
 * Снимает и возвращает одноразовый флаг из requestForcePrejoin().
 *
 * Флаг «съедается» при первом чтении: обычная перезагрузка во время звонка
 * продолжает работать с авто-входом, а после явного «Войти ещё раз» человек
 * увидит форму входа, даже если дисконнект успел пересохранить сессию.
 */
export function consumeForcePrejoinFlag(): boolean {
  if (typeof sessionStorage === 'undefined') return false

  const forced = sessionStorage.getItem(FORCE_PREJOIN_SESSION_KEY)
  sessionStorage.removeItem(FORCE_PREJOIN_SESSION_KEY)

  return Boolean(forced)
}

/** Restore only after the server verifies consent (or the organizer/legacy HR role). */
export async function resumeMeetingSession(session: MeetingSession, signal?: AbortSignal): Promise<MeetingUserChoices | null> {
  try {
    const response = await fetch('/api/meeting_consent', {
      method: 'POST', headers: {'Content-Type': 'application/json'}, signal,
      body: JSON.stringify({action: 'resume', roomName: session.roomName,
        participantName: session.username, consentReceipt: session.meetingConsent?.receipt}),
    })
    if (!response.ok) return null
    const data = await response.json()
    if (data.ok !== true) return null
    return {...sessionToUserChoices(session), meetingConsent: data.meetingConsent}
  } catch { return null }
}

export function useMeetingAutoRejoin({roomName, ready, enabled, onRejoin}: {
  roomName: string; ready: boolean; enabled: boolean; onRejoin: (choices: MeetingUserChoices) => void
}) {
  const {isMobileByUserAgent, isInAppBrowser} = useIsMobile()
  const [checking, setChecking] = useState(true)
  const forcePrejoin = useRef<boolean | null>(null)

  useEffect(() => {
    if (!ready) return
    if (!enabled || !roomName || (!isMobileByUserAgent && !isInAppBrowser)) {
      setChecking(false)
      return
    }
    try {
      // Keep this decision across StrictMode effect retries and rerenders.
      forcePrejoin.current ??= consumeForcePrejoinFlag()
    } catch {
      setChecking(false)
      return
    }
    const session = forcePrejoin.current ? null : shouldAutoRejoin(roomName)
    if (!session) { setChecking(false); return }

    let active = true
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 15000)
    setChecking(true)
    void resumeMeetingSession(session, controller.signal).then(choices => {
      if (!active) return
      clearMeetingSession()
      if (choices) onRejoin(choices)
    }).finally(() => {
      clearTimeout(timeout)
      if (active) setChecking(false)
    })
    return () => { active = false; clearTimeout(timeout); controller.abort() }
  }, [roomName, ready, enabled, onRejoin, isMobileByUserAgent, isInAppBrowser])

  return checking
}

interface UseMeetingSessionOptions {
  room: Room | null | undefined
  roomName: string
  userChoices: MeetingUserChoices
  enabled?: boolean
}

/**
 * Хук для сохранения контекста встречи.
 *
 * Когда Android/Chrome выгружает вкладку из памяти и потом пользователь
 * возвращается - страница перезагружается с нуля. Этот хук:
 *
 * 1. Сохраняет контекст активной встречи в sessionStorage
 * 2. При перезагрузке страницы позволяет автоматически переподключиться
 * 3. Работает только на мобильных устройствах (где это актуально)
 */
export function useMeetingSession({
  room,
  roomName,
  userChoices,
  enabled = true,
}: UseMeetingSessionOptions) {
  const { isMobileByUserAgent, isInAppBrowser } = useIsMobile()

  /**
   * Флаг: в этой вкладке комната хотя бы раз дошла до Connected.
   *
   * Пока флаг ложный, Disconnected очищает сессию — иначе неудачный вход
   * запомнится как состоявшаяся встреча, и следующий заход минует PreJoin.
   * Сбрасывать флаг при смене Room нельзя: ActiveRoom при retry пересоздаёт
   * комнату через roomKey, и Disconnected у замены стёр бы уже сохранённый
   * авто-вход после реальной встречи.
   */
  const wasEverConnectedRef = useRef(false)

  // Сохраняем сессию
  const saveSession = useCallback(
    (wasConnected: boolean) => {
      if (!roomName || !userChoices?.username) return

      const session: MeetingSession = {
        roomName,
        username: userChoices.username,
        audioEnabled: userChoices.audioEnabled ?? false,
        videoEnabled: userChoices.videoEnabled ?? false,
        audioDeviceId: userChoices.audioDeviceId,
        videoDeviceId: userChoices.videoDeviceId,
        timestamp: Date.now(),
        wasConnected,
        meetingConsent: userChoices.meetingConsent,
      }

      try {
        sessionStorage.setItem(getMeetingSessionKey(), JSON.stringify(session))
        console.log('[MeetingSession] Session saved', { roomName, wasConnected })
      } catch (e) {
        console.error('[MeetingSession] Error saving session:', e)
      }
    },
    [roomName, userChoices],
  )

  // Отслеживаем состояние подключения и сохраняем сессию
  useEffect(() => {
    // Активируем только на мобильных
    if (!enabled || (!isMobileByUserAgent && !isInAppBrowser)) {
      return
    }

    if (!room) return

    const handleConnectionStateChange = (state: ConnectionState) => {
      if (state === ConnectionState.Connected) {
        wasEverConnectedRef.current = true
        saveSession(true)
      } else if (state === ConnectionState.Disconnected) {
        if (wasEverConnectedRef.current) {
          saveSession(true)
        } else {
          clearMeetingSession()
        }
      }
    }

    // Сохраняем при уходе страницы в фон (перед возможным завершением работы приложения)
    const handleVisibilityChange = () => {
      if (document.hidden && room.state === ConnectionState.Connected) {
        saveSession(true)
        console.log('[MeetingSession] Saved before going to background')
      }
    }

    // Сохраняем перед выгрузкой страницы
    const handleBeforeUnload = () => {
      if (room.state === ConnectionState.Connected) {
        saveSession(true)
      }
    }

    // Сохраняем при замораживании вкладки (iOS/Android могут замораживать вкладки)
    const handleFreeze = () => {
      if (room.state === ConnectionState.Connected) {
        saveSession(true)
        console.log('[MeetingSession] Saved on freeze event')
      }
    }

    room.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChange)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)

    // Page Lifecycle API (если поддерживается)
    // @ts-ignore - freeze event не в стандартных типах
    document.addEventListener('freeze', handleFreeze)

    // Событие Connected уже могло пройти до подписки, поэтому флаг ставим и здесь.
    if (room.state === ConnectionState.Connected) {
      wasEverConnectedRef.current = true
      saveSession(true)
    }

    return () => {
      room.off(RoomEvent.ConnectionStateChanged, handleConnectionStateChange)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      // @ts-ignore - freeze event не в стандартных типах
      document.removeEventListener('freeze', handleFreeze)
    }
  }, [room, enabled, isMobileByUserAgent, isInAppBrowser, saveSession])

  return {
    saveSession,
    clearSession: clearMeetingSession,
  }
}
